import { useCallback, useMemo, useState } from "react";
import bonificacionesService from "../../../../services/bonificacionesService";
import { showToast } from "../../../../utils/showToast";
import useBonificacionesList from "./useBonificacionesList";

export default function useBonificacionesLogic() {
  const list = useBonificacionesList();
  const [updatingId, setUpdatingId] = useState(null);

  // Bonificaciones por cobrar (pendientes y no pagadas) CON FILTROS APLICADOS
  const pendientes = useMemo(() => {
    return list.bonificacionesFiltradas.filter((b) =>
      b.estado === "pendiente" || b.estado === "no_pagada"
    );
  }, [list.bonificacionesFiltradas]);

  // Bonificaciones en histórico (pagadas y anuladas) CON FILTROS APLICADOS
  const bonificacionesHistorico = useMemo(() => {
    return list.bonificacionesFiltradas.filter((b) => b.estado === "pagada" || b.estado === "anulada");
  }, [list.bonificacionesFiltradas]);

  const pendientesLoading = list.loading;
  const pendientesError = list.error;

  const actualizarEstado = useCallback(
    async (bonificacion, estado) => {
      const id = bonificacion?.id ?? bonificacion;
      if (!id || !estado) return;
      setUpdatingId(id);
      try {
        await bonificacionesService.actualizar(id, { estado });
        list.actualizarBonificacion(id, { estado });
        showToast({
          type: "success",
          message: `Bonificacion #${id} actualizada correctamente.`,
        });
      } catch (err) {
        console.error(err);
        const message = err.message || "No se pudo actualizar la bonificacion.";
        showToast({ type: "danger", message });
      } finally {
        setUpdatingId(null);
      }
    },
    [list]
  );

  const registrarDescuento = useCallback(
    async (bonificacionId, cargoAdicionalId, observacion, montoCubrir) => {
      setUpdatingId(bonificacionId);
      try {
        const result = await bonificacionesService.registrarDescuento(bonificacionId, {
          cargo_adicional_id: cargoAdicionalId,
          observacion_descuento: observacion || null,
          ...(montoCubrir ? { monto_cubrir: montoCubrir } : {}),
        });
        list.actualizarBonificacion(bonificacionId, {
          descuento: result.descuento,
          montoNeto: result.monto_neto,
        });
        // Recargar lista completa para reflejar los nuevos campos del adapter
        await list.reload?.();
        showToast({ type: "success", message: "Descuento registrado correctamente." });
        return true;
      } catch (err) {
        console.error(err);
        showToast({ type: "danger", message: err.message || "No se pudo registrar el descuento." });
        return false;
      } finally {
        setUpdatingId(null);
      }
    },
    [list]
  );

  const abonarAMatricula = useCallback(
    async (bonificacionId, montoAbonar) => {
      setUpdatingId(bonificacionId);
      try {
        const result = await bonificacionesService.abonarAMatricula(bonificacionId, montoAbonar ? { monto_abonar: montoAbonar } : {});
        await list.reload?.();
        const restante = result.monto_restante_bono != null ? Number(result.monto_restante_bono) : null;
        showToast({
          type: 'success',
          message: `Abono registrado. Ref: ${result.referencia} · $${Number(result.monto_abonado).toFixed(2)}`
            + (restante != null && restante > 0 ? ` · Bono restante: $${restante.toFixed(2)}` : ''),
        });
        return true;
      } catch (err) {
        console.error(err);
        showToast({ type: 'danger', message: err.message || 'No se pudo abonar la bonificación.' });
        return false;
      } finally {
        setUpdatingId(null);
      }
    },
    [list]
  );

  const eliminarDescuento = useCallback(
    async (bonificacionId) => {
      setUpdatingId(bonificacionId);
      try {
        await bonificacionesService.eliminarDescuento(bonificacionId);
        await list.reload?.();
        showToast({ type: "success", message: "Descuento eliminado correctamente." });
        return true;
      } catch (err) {
        console.error(err);
        showToast({ type: "danger", message: err.message || "No se pudo eliminar el descuento." });
        return false;
      } finally {
        setUpdatingId(null);
      }
    },
    [list]
  );

  return {
    ...list,
    pendientes,
    pendientesLoading,
    pendientesError,
    bonificacionesPaginadas: bonificacionesHistorico,
    actualizarEstado,
    registrarDescuento,
    eliminarDescuento,
    abonarAMatricula,
    updatingId,
  };
}
